import TEAMS from "./teams.json";
import { projectSeason, ratingLetter, teamStrength } from "./projection";

// 5 iconic title-winning seasons used as benchmarks.
// Each must exist in teams.json. `label` is a short tagline shown under the club name.
const BENCHMARK_TEAMS = [
  { club: "Bayern München", fifaVersion: 20, label: "Treble" },
  { club: "Liverpool FC", fifaVersion: 20, label: "Title" },
  { club: "Manchester City", fifaVersion: 18, label: "Centurions" },
  { club: "Real Madrid", fifaVersion: 17, label: "UCL three-peat" },
  { club: "Barcelona", fifaVersion: 15, label: "MSN" },
  //{ club: "Paris Saint-Germain", fifaVersion: 20, label: "UCL final" },
];

/**
 * Greedy auto-XI: walk the formation slots, pick the highest-overall
 * player whose positions match. Falls back to "best leftover" if no
 * positional match (rare on full squads).
 */
function buildAutoXI(teamPlayers, slots, teamMeta) {
  const sortedPlayers = [...teamPlayers].sort((a, b) => b.overall - a.overall);
  const used = new Set();
  const roster = {};

  // Carry the team metadata onto each player so chemistry math works
  // (same teamId, same fifaEdition → TEAM_5 + ERA_SAME bonuses fire).
  const augment = (player) => ({
    ...player,
    teamId: teamMeta.id,
    club: teamMeta.club,
    season: teamMeta.season,
    fifaEdition: teamMeta.fifaEdition,
  });

  for (const slot of slots) {
    const player = sortedPlayers.find((p) => {
      if (used.has(p.id)) return false;
      const positions = p.positions?.length ? p.positions : [p.pos];
      return positions.some((pos) => slot.accepts.includes(pos));
    });
    if (player) {
      roster[slot.id] = augment(player);
      used.add(player.id);
    }
  }
  for (const slot of slots) {
    if (roster[slot.id]) continue;
    const fallback = sortedPlayers.find((p) => !used.has(p.id));
    if (fallback) {
      roster[slot.id] = augment(fallback);
      used.add(fallback.id);
    }
  }
  return roster;
}

/**
 * Compute all benchmark projections for a given formation.
 * Returns: [{ club, season, label, wins, draws, losses, rating, strength }]
 */
export function getBenchmarkProjections(slots) {
  if (!slots) return [];
  const out = [];
  for (const bm of BENCHMARK_TEAMS) {
    const team = TEAMS.find(
      (t) => t.club === bm.club && t.fifaVersion === bm.fifaVersion,
    );
    if (!team) continue; // silently skip if not in data
    const roster = buildAutoXI(team.players, slots, team);
    if (Object.keys(roster).length === 0) continue;
    const season = projectSeason(roster, slots);
    const strength = teamStrength(roster, slots);
    out.push({
      club: team.club,
      season: team.season,
      label: bm.label,
      wins: season.wins,
      draws: season.draws,
      losses: season.losses,
      rating: ratingLetter(season),
      strength,
    });
  }
  return out;
}
